import { Bell, ClockCounterClockwise } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";

import { useDashboard } from "@/hooks";
import { ContentLayout } from "@/layout";
import { ROUTES } from "@/router";
import { Heading, Loader } from "@/ui";
import { formatDateToDDMMYYYY } from "@/utils";

const StatCard = ({
  label,
  value,
  sub,
  hint,
}: {
  label: string;
  value: string | number;
  sub?: string;
  hint?: string;
}) => (
  <div className="flex flex-col gap-1 rounded-xl border border-neutral-200 bg-white p-5 shadow-sm">
    <span className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-neutral-500">
      {label}
      {hint ? (
        <span className="group relative inline-flex cursor-help">
          <span className="flex h-3.5 w-3.5 items-center justify-center rounded-full border border-neutral-400 text-[9px] font-bold text-neutral-400">
            ?
          </span>
          <span className="pointer-events-none absolute left-0 top-5 z-20 hidden w-52 whitespace-normal rounded-lg bg-neutral-900 p-2 text-[11px] font-normal normal-case leading-snug text-white shadow-lg group-hover:block">
            {hint}
          </span>
        </span>
      ) : null}
    </span>
    <span className="text-3xl font-bold text-primary-950">{value}</span>
    {sub ? <span className="text-sm text-neutral-500">{sub}</span> : null}
  </div>
);

export const Home = () => {
  const navigate = useNavigate();
  const { useStats } = useDashboard();
  const { data, isLoading, isError } = useStats();
  const stats = data?.data;

  return (
    <ContentLayout className="flex flex-col gap-y-6">
      <Heading level={2} heading="Dashboard" className="text-3xl" />

      {isError ? (
        <div className="flex justify-center py-16">
          <p className="text-sm text-neutral-500">
            Unable to load the dashboard right now. Please refresh to try again.
          </p>
        </div>
      ) : isLoading || !stats ? (
        <div className="flex justify-center py-16">
          <Loader />
        </div>
      ) : (
        <>
          {/* Headline stats */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <StatCard
              label="Users"
              value={stats.users.total.toLocaleString()}
              sub={`+${stats.users.new_this_week} this week · +${stats.users.new_this_month} this month`}
            />
            <StatCard
              label="People with a plan"
              hint="Counts people, not plans. A cancelled app-store subscription is only removed when that person next opens the app, so this can run slightly high. RevenueCat has the exact subscriber count."
              value={stats.plan_holders.total.toLocaleString()}
              sub={`${stats.plan_holders.paying.toLocaleString()} paying · ${stats.plan_holders.free_access.toLocaleString()} free access`}
            />
            <button
              type="button"
              onClick={() =>
                navigate(ROUTES.coursesAndMaterials, {
                  state: { tab: "access-requests" },
                })
              }
              className="flex flex-col gap-1 rounded-xl border border-neutral-200 bg-white p-5 text-left shadow-sm transition hover:border-primary-300 hover:shadow"
            >
              <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-neutral-500">
                <Bell size={14} /> Needs attention
              </span>
              <span className="text-3xl font-bold text-primary-950">
                {stats.pending_access_requests}
              </span>
              <span className="text-sm text-neutral-500">
                pending access requests →
              </span>
            </button>
          </div>

          {/* Recent activity */}
          <section className="rounded-xl border border-neutral-200 bg-white p-6 shadow-sm">
            <h3 className="mb-4 flex items-center gap-2 text-lg font-bold text-primary-950">
              <ClockCounterClockwise size={20} className="text-tertiary-600" />
              Recent admin activity
            </h3>
            {stats.recent_activity.length === 0 ? (
              <p className="text-sm text-neutral-500">
                No recent admin activity.
              </p>
            ) : (
              <ul className="flex flex-col divide-y divide-neutral-100">
                {stats.recent_activity.map((item, index) => (
                  <li
                    key={`${item.created_at}-${index}`}
                    className="flex items-center justify-between gap-4 py-3"
                  >
                    <span className="text-sm text-neutral-800">
                      <span className="font-medium text-neutral-900">
                        {item.causer ?? "Someone"}
                      </span>{" "}
                      {item.action} {item.subject_type}{" "}
                      {item.subject_name ? (
                        <span className="font-medium text-neutral-900">
                          {item.subject_name}
                        </span>
                      ) : null}
                    </span>
                    <span className="whitespace-nowrap text-xs text-neutral-400">
                      {item.created_at
                        ? formatDateToDDMMYYYY(new Date(item.created_at))
                        : ""}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </ContentLayout>
  );
};
